import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { fetchSlots, createSlot, deleteSlot, updateSlot } from '../services/slots.service';
import { useAuth } from '../context/AuthContext';
import { LogOut, Clock, Menu } from 'lucide-react';
import { Link } from 'react-router-dom';

const localizer = momentLocalizer(moment);

const SlotManager = () => {
  const [slots, setSlots] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(moment.tz('America/New_York').toDate());

  const loadSlots = async () => {
    setLoading(true);
    let allSlots = [];
    let nextToken = null;
    try {
      do {
        // Pass a default limit, adjust if needed, or make it a constant
        const result = await fetchSlots({}, 100, nextToken); 
        if (result && result.items) {
          allSlots = [...allSlots, ...result.items];
        }
        nextToken = result.nextToken;
      } while (nextToken);
      setSlots(allSlots); // Set the accumulated array of slots
    } catch (err) {
      console.error('Error loading slots:', err);
      setSlots([]); // Set to empty array in case of error to prevent .map error
    }
    setLoading(false);
  };

  const handleAddSlot = async () => {
    if (!newDate || !startTime) return;

    const [hour, minute] = startTime.split(':').map(Number);
    const startHour = hour;
    const startMinute = minute;

    // Ensure the slot is between 6am and 8pm
    // const totalMinutes = startHour * 60 + startMinute;
    // if (totalMinutes < 6 * 60 || totalMinutes + 90 > 20 * 60) {
    //   alert('Slot must be between 6:00 AM and 8:00 PM');
    //   return;
    // }

    const start = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;

    const totalStartMinutes = hour * 60 + minute;
    const totalEndMinutes = totalStartMinutes + 90;
    const endHour = Math.floor(totalEndMinutes / 60);
    const endMinute = totalEndMinutes % 60;
    
    const end = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}:00`;
    

    const input = {
      date: newDate,
      startTime: start,
      endTime: end,
      isBooked: false,
    };

    try {
      await createSlot(input);
      await loadSlots();
    } catch (err) {
      console.error('Failed to create slot:', err);
    }
  };

// Remaining handlers

  const handleDeleteSlot = async (slotId) => {
    try {
      await deleteSlot(slotId);
      await loadSlots();
    } catch (err) {
      console.error('Error deleting slot:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const convertToEvents = (slots) => {
    return slots.map((slot) => {
      //const start = moment.tz(`${slot.date}T${slot.startTime}`, 'America/New_York').toDate();
      //const end = moment.tz(`${slot.date}T${slot.endTime}`, 'America/New_York').toDate();
      const start = moment(`${slot.date}T${slot.startTime}`).toDate();
      const end = moment(`${slot.date}T${slot.endTime}`).toDate();
      
      return {
        id: slot.id,
        title: `${slot.isBooked ? 'Booked' : 'Open'}`,
        start,
        end,
        allDay: false
      };
    });
  };

  const formats = {
    eventTimeRangeFormat: () => null,
  };

  useEffect(() => {
    loadSlots();
  }, []);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`bg-gray-900 text-white flex flex-col justify-between transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div>
          <div className="p-4 flex items-center justify-between">
            <h1 className="text-xl font-bold whitespace-nowrap overflow-hidden transition-all duration-300">
              {sidebarOpen ? 'Submission Hub' : 'SH'}
            </h1>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
              <Menu size={20} />
            </button>
          </div>
          <nav className="mt-4">
            <ul>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/manage-slots" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Slots</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/manage-appointments" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Appointments</span>}
                </Link>
              </li>                
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Submissions</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <Link to="/message-templates" className="flex items-center w-full text-white">
                <Clock size={20} />
                {sidebarOpen && <span className="ml-3">Manage Message Templates</span>}
              </Link>
            </li>               
            </ul>
          </nav>
        </div>
        <div className="p-4">
          <div className="flex items-center cursor-pointer hover:bg-gray-800 p-2 rounded" onClick={handleLogout}>
            <LogOut size={20} />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm px-4 md:px-6 py-4 flex items-center justify-end">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
              {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-gray-700">{currentUser?.email || 'User'}</p>
              <p className="text-xs text-gray-500">Admin</p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
          <div className="w-full">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Manage Appointment Slots</h2>

            <div className="flex flex-wrap gap-4 mb-6">
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="border rounded px-3 py-2 w-full sm:w-auto"
              />
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="border rounded px-3 py-2 w-full sm:w-auto"
              />
              <button
                onClick={handleAddSlot}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full sm:w-auto"
              >
                Add Slot
              </button>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : (
              <div className="overflow-x-auto">
                <Calendar
                  localizer={localizer}
                  events={convertToEvents(slots)}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: '75vh', minWidth: '600px' }}
                  view={currentView}
                  onView={setCurrentView}
                  date={currentDate}
                  onNavigate={setCurrentDate}
                  defaultView="week"
                  views={['month', 'week', 'day']}
                  timeslots={1}
                  step={30}
                  getNow={() => moment.tz('America/New_York').toDate()}
                  showMultiDayTimes={false}
                  formats={formats}
                  onSelectEvent={(event) => {
                    if (window.confirm(`Delete this slot (${event.title})?`)) {
                      handleDeleteSlot(event.id);
                    }
                  }}                  
                  components={{
                    timeGutterHeader: () => <span>EST</span>
                  }}
                  {...(currentView !== 'month' && {
                    minTime: new Date(1970, 1, 1, 8, 0),
                    maxTime: new Date(1970, 1, 1, 17, 0)
                  })}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SlotManager;
